"use client";

import { c as _c } from "react/compiler-runtime";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { publicEnv } from "@/config/env";
import { MEMES_CONTRACT } from "@/constants/constants";
import type { DBResponse } from "@/entities/IDBResponse";
import type { NFT } from "@/entities/INFT";
import { fetchUrl } from "@/services/6529api";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { getMemeCardCountLabel } from "./wave-card-set-tdh.helpers";
const METADATA_CHUNK_SIZE = 100;
type MemeCardMetadata = Pick<NFT, "id" | "name" | "thumbnail" | "scaled" | "image" | "icon">;
interface WaveRatingCardSetTdhDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly tokenIds: readonly number[];
}
function getTokenIdChunks(tokenIds: readonly number[]): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < tokenIds.length; i += METADATA_CHUNK_SIZE) {
    chunks.push(tokenIds.slice(i, i + METADATA_CHUNK_SIZE));
  }
  return chunks;
}
function getImageSrc(metadata: MemeCardMetadata | undefined): string | null {
  if (!metadata) {
    return null;
  }
  return metadata.thumbnail || metadata.scaled || metadata.image || metadata.icon || null;
}
function getCardName(metadata: MemeCardMetadata | undefined, tokenId: number): string {
  return metadata?.name || `Meme card #${tokenId}`;
}
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}
async function fetchMemeCardMetadata(tokenIds: readonly number[], signal: AbortSignal): Promise<Map<number, MemeCardMetadata>> {
  const responses = await Promise.all(getTokenIdChunks(tokenIds).map(chunk => fetchUrl<DBResponse<MemeCardMetadata>>(`${publicEnv.API_ENDPOINT}/api/nfts?contract=${MEMES_CONTRACT}&id=${chunk.join(",")}&page_size=${chunk.length}`, {
    signal
  })));
  const metadataById = new Map<number, MemeCardMetadata>();
  for (const response of responses) {
    const nfts = Array.isArray(response.data) ? response.data : [];
    for (const nft of nfts) {
      if (Number.isInteger(nft.id)) {
        metadataById.set(nft.id, nft);
      }
    }
  }
  return metadataById;
}
function MemeCardSetLink(t0) {
  const $ = _c(17);
  const {
    metadata,
    tokenId
  } = t0;
  let t1;
  if ($[0] !== metadata) {
    t1 = getImageSrc(metadata);
    $[0] = metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const imageSrc = t1;
  let t2;
  if ($[2] !== metadata || $[3] !== tokenId) {
    t2 = getCardName(metadata, tokenId);
    $[2] = metadata;
    $[3] = tokenId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const cardName = t2;
  const t3 = `/the-memes/${tokenId}`;
  let t4;
  if ($[5] !== cardName || $[6] !== imageSrc || $[7] !== metadata) {
    t4 = imageSrc ? <Image src={imageSrc} alt={metadata?.name ? `${metadata.name} Meme card` : cardName} width={48} height={48} className="tw-h-12 tw-w-12 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-object-cover" /> : <div className="tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-iron-800 tw-text-xs tw-font-semibold tw-text-iron-300">M</div>;
    $[5] = cardName;
    $[6] = imageSrc;
    $[7] = metadata;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== cardName || $[10] !== metadata || $[11] !== tokenId) {
    t5 = <div className="tw-min-w-0">{metadata ? <><p title={cardName} className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-50">{cardName}</p><p className="tw-m-0 tw-text-xs tw-font-medium tw-text-iron-400">#{tokenId}</p></> : <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-iron-50">#{tokenId}</p>}</div>;
    $[9] = cardName;
    $[10] = metadata;
    $[11] = tokenId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <Link href={t3} prefetch={false} className="tw-flex tw-min-h-[68px] tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/50 tw-p-2.5 tw-text-iron-100 tw-no-underline tw-transition-colors hover:tw-border-iron-700 hover:tw-bg-iron-900">{t4}{t5}</Link>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
export default function WaveRatingCardSetTdhDialog(t0) {
  const $ = _c(27);
  const {
    isOpen,
    onClose,
    tokenIds
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [metadataById, setMetadataById] = useState(t1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMetadataError, setHasMetadataError] = useState(false);
  let t2;
  if ($[1] !== tokenIds.length) {
    t2 = getMemeCardCountLabel(tokenIds.length);
    $[1] = tokenIds.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const countLabel = t2;
  let t3;
  let t4;
  if ($[3] !== isOpen || $[4] !== tokenIds) {
    t3 = () => {
      if (!isOpen) {
        return;
      }
      setMetadataById(new Map());
      setHasMetadataError(false);
      if (tokenIds.length === 0) {
        setIsLoading(false);
        return;
      }
      let cancelled = false;
      const controller = new AbortController();
      setIsLoading(true);
      fetchMemeCardMetadata(tokenIds, controller.signal).then(nextMetadataById => {
        if (!cancelled) {
          setMetadataById(nextMetadataById);
        }
      }).catch(error => {
        if (cancelled || isAbortError(error)) {
          return;
        }
        console.error("Failed to fetch Meme card metadata", error);
        setMetadataById(new Map());
        setHasMetadataError(true);
      }).finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
      return () => {
        cancelled = true;
        controller.abort();
      };
    };
    t4 = [isOpen, tokenIds];
    $[3] = isOpen;
    $[4] = tokenIds;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== countLabel) {
    t5 = <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-iron-200">{countLabel}</p>;
    $[7] = countLabel;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isLoading) {
    t6 = isLoading && <div aria-label="Loading Meme card details" className="tw-size-4 tw-flex-shrink-0 tw-animate-spin tw-rounded-full tw-border-2 tw-border-iron-700 tw-border-t-primary-400" />;
    $[9] = isLoading;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="tw-flex tw-min-h-6 tw-items-center tw-justify-between tw-gap-3">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== hasMetadataError) {
    t8 = hasMetadataError && <p className="tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/60 tw-px-3 tw-py-2 tw-text-sm tw-text-iron-300">Card details could not load. Token links are still available.</p>;
    $[14] = hasMetadataError;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== metadataById || $[17] !== tokenIds) {
    t9 = tokenIds.length === 0 ? <p className="tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900/60 tw-px-3 tw-py-4 tw-text-sm tw-text-iron-300">No Meme cards are configured for this wave.</p> : <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">{tokenIds.map(tokenId => <MemeCardSetLink key={tokenId} metadata={metadataById.get(tokenId)} tokenId={tokenId} />)}</div>;
    $[16] = metadataById;
    $[17] = tokenIds;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-4 tw-px-4 sm:tw-px-6">{t7}{t8}{t9}</div>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== isOpen || $[24] !== onClose || $[25] !== t10) {
    t11 = <MobileWrapperDialog title="Meme cards in TDH set" isOpen={isOpen} onClose={onClose} fixedHeight={true} showScrollbar={true} tabletModal={true} maxWidthClass="md:tw-max-w-2xl">{t10}</MobileWrapperDialog>;
    $[23] = isOpen;
    $[24] = onClose;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}